import React, { useCallback, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import CustomModal from "../CustomModal";
import CustomButton from "../../Buttons/Button/CustomButton";
import CustomSectionTitle from "../../SectionTitle/CustomSectionTitle";
import { colors } from "../../theme/colors";
import type {
  CustomCsvUploadModalProps,
  CsvValidationResult,
  CsvUploadSummary,
} from "./types";

const CustomCsvUploadModal: React.FC<CustomCsvUploadModalProps> = ({
  open,
  onClose,
  title,
  description,
  rules,
  onDownloadEmptyTemplate,
  onDownloadCurrent,
  currentDataLabel,
  currentDataTitle,
  onParseAndValidate,
  onConfirmUpload,
  isSubmitting = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<CsvValidationResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setFile(null);
    setIsDragging(false);
    setIsValidating(false);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    reset();
    onClose();
  }, [isSubmitting, onClose, reset]);

  const runValidation = useCallback(
    async (selected: File) => {
      setFile(selected);
      setIsValidating(true);
      setResult(null);
      try {
        const r = await onParseAndValidate(selected);
        setResult(r);
      } catch (e) {
        setResult({
          isValid: false,
          rowCount: 0,
          errors: [
            {
              line: 0,
              message:
                e instanceof Error
                  ? e.message
                  : "ファイルの読み込みに失敗しました",
            },
          ],
          warnings: [],
          preview: [],
        });
      } finally {
        setIsValidating(false);
      }
    },
    [onParseAndValidate],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) runValidation(selected);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      setResult({
        isValid: false,
        rowCount: 0,
        errors: [
          { line: 0, message: "ファイルは 1 つだけドロップしてください" },
        ],
        warnings: [],
        preview: [],
      });
      return;
    }
    const dropped = files[0];
    if (!dropped.name.toLowerCase().endsWith(".csv")) {
      setResult({
        isValid: false,
        rowCount: 0,
        errors: [{ line: 0, message: ".csv ファイルのみ受け付けます" }],
        warnings: [],
        preview: [],
      });
      return;
    }
    runValidation(dropped);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  const handleConfirm = async () => {
    if (!file || !result?.isValid) return;
    await onConfirmUpload(file);
    reset();
  };

  return (
    <CustomModal
      openModal={open}
      handleCloseModal={handleClose}
      title={title}
      modalType="registerModal"
      addTitleBorder
      hideCloseButton={isSubmitting}
      dynamicHeight
      sx={{
        width: {
          xs: "calc(100vw - 16px)",
          sm: "min(648px, calc(100vw - 32px))",
        },
      }}
    >
      <Box
        className="registerModalBody"
        data-testid="csv-upload-modal-body"
        sx={{
          maxHeight: { xs: "calc(100vh - 176px)", sm: "calc(100vh - 196px)" },
          overflowY: "auto",
          overflowX: "hidden !important",
          width: "calc(100% + 16px) !important",
          mr: "-16px !important",
          pr: "16px !important",
          pb: "20px !important",
        }}
      >
        {description && (
          <Typography
            sx={{
              fontSize: 14,
              color: colors.text.primary,
              mb: 1.5,
            }}
          >
            {description}
          </Typography>
        )}

        {/* CSV ダウンロード */}
        <Box sx={{ mb: 2 }}>
          <SectionHeader label="CSVダウンロード" />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
            mb: 2,
          }}
        >
          <DownloadCard
            testId="csv-download-empty-template"
            icon={<DescriptionIcon />}
            title="空テンプレート"
            subtitle="フォーマットのみ"
            onClick={onDownloadEmptyTemplate}
            disabled={false}
          />
          <DownloadCard
            testId="csv-download-current"
            icon={<DescriptionIcon />}
            title={currentDataTitle ?? "現在のデータ"}
            subtitle={
              onDownloadCurrent && currentDataLabel
                ? currentDataLabel
                : "データなし"
            }
            onClick={onDownloadCurrent ?? (() => undefined)}
            disabled={!onDownloadCurrent}
          />
        </Box>

        {/* アップロードルール (文字数が多いので折りたたみ。既定は閉じる)。
            開閉でアップロードセクションとの余白がブレないよう、外側 Box で mb を持つ。 */}
        {rules.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Accordion
              disableGutters
              defaultExpanded={false}
              slotProps={{
                transition: { timeout: 180, unmountOnExit: true } as never,
              }}
              sx={{
                backgroundColor: colors.background.lighter,
                borderRadius: "8px",
                boxShadow: "none",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={null}
                sx={{
                  px: 1,
                  minHeight: 36,
                  "& .MuiAccordionSummary-content": {
                    my: 0.5,
                    alignItems: "center",
                    gap: 1,
                  },
                  "& .rules-arrow svg": {
                    transform: "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  },
                  "&.Mui-expanded .rules-arrow svg": {
                    transform: "rotate(0deg)",
                  },
                }}
              >
                <Box
                  className="rules-arrow"
                  sx={{
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "6px",
                    color: colors.text.secondary,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "#DBF8FF",
                      "& svg": { color: "#0E9DBC" },
                    },
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 20, color: "inherit" }} />
                </Box>
                <InfoOutlinedIcon
                  sx={{ fontSize: 18, color: colors.status.info }}
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.text.primary,
                  }}
                >
                  アップロードルール
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.25 }}>
                <Stack component="ul" sx={{ m: 0, pl: 2.5, gap: 0.5 }}>
                  {rules.map((rule, idx) => (
                    <Box
                      component="li"
                      key={idx}
                      sx={{ fontSize: 12.5, lineHeight: 1.45 }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, color: colors.text.primary }}
                      >
                        {rule.title.split(/(（必須）)/).map((part, i) =>
                          part === "（必須）" ? (
                            <Box
                              component="span"
                              key={i}
                              sx={{ color: colors.status.error }}
                            >
                              {part}
                            </Box>
                          ) : (
                            part
                          ),
                        )}
                        :
                      </Box>{" "}
                      <Box
                        component="span"
                        sx={{ color: colors.text.secondary }}
                      >
                        {rule.description}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* アップロードエリア (CustomFileUploader の .areaToDropImage と同等のスタイル) */}
        <Box sx={{ mb: 2.5 }}>
          <SectionHeader label="アップロード" />
        </Box>
        <Box
          data-testid="csv-dropzone"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            if (!file && !isSubmitting) inputRef.current?.click();
          }}
          sx={{
            border: file ? "1px solid" : "1px dashed",
            borderColor: isDragging
              ? colors.primary.light
              : file
                ? colors.border.light
                : colors.primary.main,
            borderRadius: file ? "8px" : "10px",
            px: file ? 1 : 2.5,
            py: file ? 1 : 2.75,
            minHeight: file ? 40 : 200,
            mb: result || isValidating ? 1.5 : 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: isDragging
              ? colors.primary.light
              : colors.background.default,
            transition: "background-color 0.2s ease, border-color 0.2s ease",
            cursor: file || isSubmitting ? "default" : "pointer",
            "&:hover":
              file || isSubmitting
                ? undefined
                : { backgroundColor: colors.background.selected },
          }}
        >
          {file ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
              sx={{ width: "100%", minWidth: 0 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ minWidth: 0 }}
              >
                <DescriptionIcon
                  sx={{ fontSize: 28, color: colors.primary.main }}
                />
                <Box sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: colors.text.primary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, color: colors.text.secondary }}
                  >
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              </Stack>
              <Box
                role="button"
                aria-label="ファイルを解除"
                onClick={(e) => {
                  e.stopPropagation();
                  reset();
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  cursor: "pointer",
                  color: colors.text.secondary,
                  "&:hover": {
                    backgroundColor: colors.background.hover,
                    color: colors.text.primary,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </Box>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={0.75}>
              <UploadFileIcon
                sx={{
                  fontSize: 34,
                  color: colors.primary.main,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.text.primary,
                }}
              >
                ここにドラッグ＆ドロップ
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: colors.text.secondary,
                }}
              >
                または
              </Typography>
              <input
                ref={inputRef}
                data-testid="csv-file-input"
                type="file"
                accept=".csv"
                multiple={false}
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <Box
                role="button"
                aria-label="ファイルを選択"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                sx={{
                  mt: 0.5,
                  px: 1.5,
                  height: 25,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  border: `1px solid ${colors.primary.main}`,
                  borderRadius: "11.6px",
                  color: colors.primary.main,
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                クリックして参照
              </Box>
            </Stack>
          )}
        </Box>

        {/* バリデーション結果 */}
        {isValidating && (
          <Typography
            sx={{
              textAlign: "center",
              color: colors.text.secondary,
              fontSize: 13,
              py: 2,
            }}
          >
            検証中...
          </Typography>
        )}

        {result && !isValidating && (
          <Box
            data-testid="csv-validation-result"
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            <ResultSummary result={result} />
            {result.errors.length > 0 && (
              <IssueList
                testId="csv-validation-errors"
                title="エラー"
                tone="error"
                items={result.errors}
              />
            )}
            {result.warnings.length > 0 && (
              <IssueList
                testId="csv-validation-warnings"
                title="警告"
                tone="warning"
                items={result.warnings}
              />
            )}
            {result.notices && result.notices.length > 0 && (
              <IssueList
                testId="csv-validation-notices"
                title="注意喚起"
                tone="notice"
                items={result.notices}
              />
            )}
            {result.summary && <SummaryPanel summary={result.summary} />}
          </Box>
        )}
      </Box>

      <Box className="registerModalFooter">
        <Box data-testid="csv-cancel-button">
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
        </Box>
        <Box data-testid="csv-confirm-button">
          <CustomButton
            label="アップロード"
            buttonCategory="submit"
            onClick={handleConfirm}
            isLoading={isSubmitting}
            disabled={!file || !result?.isValid || isSubmitting}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ mb: 1 }}>
    <CustomSectionTitle title={label} size="small" />
  </Box>
);

const DownloadCard: React.FC<{
  testId?: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled: boolean;
}> = ({ testId, icon, title, subtitle, onClick, disabled }) => (
  <Box
    data-testid={testId}
    role="button"
    aria-label={`${title} をダウンロード`}
    aria-disabled={disabled}
    onClick={disabled ? undefined : onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: 1.25,
      borderRadius: "8px",
      border: "1px solid",
      borderColor: colors.border.light,
      backgroundColor: "#FFFFFF",
      color: disabled ? colors.text.disabled : colors.text.primary,
      opacity: disabled ? 0.55 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
      "&:hover": disabled
        ? undefined
        : {
            borderColor: colors.primary.main,
            backgroundColor: colors.background.selected,
            color: colors.primary.main,
          },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ display: "flex", alignItems: "center", color: "inherit" }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "inherit" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 11, color: colors.text.secondary }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        color: "inherit",
        pointerEvents: "none",
      }}
    >
      <DownloadIcon sx={{ fontSize: 20 }} />
    </Box>
  </Box>
);

const ResultSummary: React.FC<{ result: CsvValidationResult }> = ({
  result,
}) => {
  const ok = result.isValid;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        p: 1,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: ok ? colors.status.success : colors.status.error,
        backgroundColor: ok
          ? "rgba(76, 175, 80, 0.08)"
          : colors.status.errorBackground,
      }}
    >
      {ok ? (
        <CheckCircleOutlineIcon sx={{ color: colors.status.success }} />
      ) : (
        <ErrorOutlineIcon sx={{ color: colors.status.error }} />
      )}
      <Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: ok ? colors.status.success : colors.status.error,
          }}
        >
          {ok ? "検証 OK" : "エラーがあります"}
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.text.secondary }}>
          {result.rowCount}行のデータ
          {result.errors.length > 0 && ` / ${result.errors.length}件のエラー`}
          {result.warnings.length > 0 && ` / ${result.warnings.length}件の警告`}
        </Typography>
      </Box>
    </Stack>
  );
};

const IssueList: React.FC<{
  testId?: string;
  title: string;
  tone: "error" | "warning" | "notice";
  items: { line: number; message: string }[];
}> = ({ testId, title, tone, items }) => {
  const toneColor =
    tone === "error"
      ? colors.status.error
      : tone === "warning"
        ? colors.status.warning
        : colors.status.info;
  const toneBg =
    tone === "error"
      ? colors.status.errorBackground
      : tone === "warning"
        ? "rgba(255, 152, 0, 0.08)"
        : "rgba(33, 150, 243, 0.08)";
  return (
    <Box
      data-testid={testId}
      sx={{
        border: "1px solid",
        borderColor: toneColor,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ backgroundColor: toneBg, px: 2, py: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: toneColor }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 160, overflowY: "auto" }}>
        {items.map((it, idx) => (
          <Stack
            key={idx}
            direction="row"
            spacing={1}
            sx={{
              px: 2,
              py: 1,
              borderTop:
                idx === 0 ? "none" : `1px solid ${colors.border.light}`,
              fontSize: 12,
            }}
          >
            <Box
              sx={{
                px: 0.75,
                borderRadius: "4px",
                backgroundColor: toneBg,
                color: toneColor,
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: "nowrap",
                height: 20,
                display: "flex",
                alignItems: "center",
              }}
            >
              {it.line === 0 ? "全体" : `行 ${it.line}`}
            </Box>
            <Typography sx={{ fontSize: 12, color: colors.text.secondary }}>
              {it.message}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

const SummaryPanel: React.FC<{ summary: CsvUploadSummary }> = ({ summary }) => {
  const fmt = (n: number) => n.toLocaleString();
  const newExpenseItemCount =
    summary.newExpenseCategoryItems?.reduce(
      (sum, item) => sum + item.itemNames.length,
      0,
    ) ?? 0;

  const accordionSx = {
    border: `1px solid ${colors.border.light}`,
    borderRadius: "10px",
    boxShadow: "none",
    "&:before": { display: "none" },
    "&.Mui-expanded": { borderRadius: "10px", margin: 0 },
  };

  const accordionSummarySx = {
    px: 1,
    py: "0 !important",
    height: "36px !important",
    minHeight: "36px !important",
    maxHeight: "36px !important",
    borderRadius: "10px",
    "& .MuiAccordionSummary-content": {
      margin: "0 !important",
      minHeight: "0 !important",
    },
    "&.Mui-expanded": {
      borderRadius: "10px 10px 0 0",
      height: "36px !important",
      minHeight: "36px !important",
      maxHeight: "36px !important",
    },
    "&.Mui-expanded .MuiAccordionSummary-content": {
      margin: "0 !important",
    },
  };

  const arrowBoxSx = {
    width: 26,
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    transition: "all 0.2s",
    flexShrink: 0,
    color: colors.text.primary,
    ".Mui-expanded &": {
      backgroundColor: colors.background.selected,
      color: colors.primary.main,
    },
    ".MuiAccordionSummary-root:hover &": {
      backgroundColor: colors.background.selected,
      "& svg": { color: colors.primary.main },
    },
  };

  return (
    <Box
      data-testid="csv-upload-summary"
      sx={{
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          backgroundColor: colors.background.lighter,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${colors.border.light}`,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          取込みプレビュー
        </Typography>
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Stack direction="row" spacing={3} data-testid="csv-summary-counts">
          <Box>
            <Typography sx={{ fontSize: 11, color: colors.text.secondary }}>
              予算行
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {fmt(summary.budgetRowCount)} 件
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, color: colors.text.secondary }}>
              実績行
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {fmt(summary.actualRowCount)} 件
            </Typography>
          </Box>
          {summary.newCategoryNames.length > 0 &&
            !summary.newExpenseCategoryItems?.length && (
              <Box data-testid="csv-summary-new-categories">
                <Typography sx={{ fontSize: 11, color: colors.text.secondary }}>
                  自動作成される科目
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {summary.newCategoryNames.join(" / ")}
                </Typography>
              </Box>
            )}
        </Stack>

        {summary.newExpenseCategoryItems &&
          summary.newExpenseCategoryItems.length > 0 && (
            <Accordion
              data-testid="csv-summary-new-expense-category-items"
              disableGutters
              slotProps={{
                transition: { timeout: 180, unmountOnExit: true } as never,
              }}
              sx={accordionSx}
            >
              <AccordionSummary expandIcon={null} sx={accordionSummarySx}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <Box sx={arrowBoxSx}>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 20,
                        color: "inherit",
                        transition: "transform 0.2s ease",
                        transform: "rotate(-90deg)",
                        ".Mui-expanded &": { transform: "rotate(0deg)" },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.text.primary,
                    }}
                  >
                    新規追加されるカテゴリ・明細項目 ({newExpenseItemCount} 件)
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box
                  sx={{
                    maxHeight: 140,
                    overflowY: "auto",
                    borderTop: `1px solid ${colors.border.light}`,
                    borderRadius: "0 0 10px 10px",
                  }}
                >
                  {summary.newExpenseCategoryItems.map((item, i) => (
                    <Stack
                      key={item.category}
                      direction="row"
                      spacing={1}
                      sx={{
                        px: 1.25,
                        py: 0.5,
                        borderTop:
                          i === 0 ? "none" : `1px solid ${colors.border.light}`,
                        fontSize: 11.5,
                      }}
                    >
                      <Box sx={{ color: colors.text.secondary, minWidth: 64 }}>
                        カテゴリ
                      </Box>
                      <Box
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          minWidth: 120,
                        }}
                      >
                        {item.category}
                      </Box>
                      <Box sx={{ color: colors.text.secondary, minWidth: 58 }}>
                        明細項目
                      </Box>
                      <Box sx={{ color: colors.text.primary, fontWeight: 500 }}>
                        {item.itemNames.join(" / ")}
                      </Box>
                    </Stack>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

        {summary.overwrites.length > 0 && (
          <Accordion
            data-testid="csv-summary-overwrites"
            disableGutters
            slotProps={{
              transition: { timeout: 180, unmountOnExit: true } as never,
            }}
            sx={accordionSx}
          >
            <AccordionSummary expandIcon={null} sx={accordionSummarySx}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Box sx={arrowBoxSx}>
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 20,
                      color: "inherit",
                      transition: "transform 0.2s ease",
                      transform: "rotate(-90deg)",
                      ".Mui-expanded &": { transform: "rotate(0deg)" },
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.status.warning,
                  }}
                >
                  既存データへ上書きされる行 ({summary.overwrites.length} 件)
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box
                sx={{
                  maxHeight: 140,
                  overflowY: "auto",
                  borderTop: `1px solid ${colors.border.light}`,
                  borderRadius: "0 0 10px 10px",
                }}
              >
                {summary.overwrites.map((o, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      borderTop:
                        i === 0 ? "none" : `1px solid ${colors.border.light}`,
                      fontSize: 11.5,
                    }}
                  >
                    <Box sx={{ color: colors.text.secondary, minWidth: 50 }}>
                      {o.fiscalYear}年度
                    </Box>
                    <Box sx={{ color: colors.text.secondary, minWidth: 36 }}>
                      {o.rowKind}
                    </Box>
                    <Box sx={{ color: colors.text.primary, fontWeight: 500 }}>
                      {o.category}
                      {o.detailLabel ? ` / ${o.detailLabel}` : ""}
                    </Box>
                    {o.existingSummary && (
                      <Box sx={{ color: colors.text.secondary, ml: "auto" }}>
                        既存: {o.existingSummary}
                      </Box>
                    )}
                  </Stack>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {summary.categoryAggregates.length > 0 && (
          <Box
            data-testid="csv-summary-aggregates"
            sx={{
              border: `1px solid ${colors.border.light}`,
              borderRadius: "6px",
              overflowX: "auto",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 11.5,
                "& th:last-of-type, & td:last-of-type": {
                  borderRight: "none",
                },
              }}
            >
              <Box
                component="thead"
                sx={{ backgroundColor: colors.background.light }}
              >
                <Box component="tr">
                  {[
                    "年度",
                    "大カテゴリ",
                    "科目",
                    "予算合計",
                    "実績合計",
                    "差異",
                  ].map((h) => (
                    <Box
                      component="th"
                      key={h}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        textAlign:
                          h === "年度" || h === "大カテゴリ" || h === "科目"
                            ? "left"
                            : "right",
                        fontWeight: 600,
                        borderRight: `1px solid ${colors.border.light}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {summary.categoryAggregates.map((a, i) => {
                  const diffColor =
                    a.diff < 0
                      ? colors.status.error
                      : a.diff > 0
                        ? colors.text.primary
                        : colors.text.secondary;
                  return (
                    <Box
                      component="tr"
                      key={`${a.fiscalYear}|${a.category}|${i}`}
                      sx={{ borderTop: `1px solid ${colors.border.light}` }}
                    >
                      <Box component="td" sx={cellSx}>
                        {a.fiscalYear}
                      </Box>
                      <Box component="td" sx={cellSx}>
                        {a.group}
                      </Box>
                      <Box component="td" sx={cellSx}>
                        {a.category}
                      </Box>
                      <Box component="td" sx={cellRightSx}>
                        {fmt(a.budgetTotal)}
                      </Box>
                      <Box component="td" sx={cellRightSx}>
                        {fmt(a.actualTotal)}
                      </Box>
                      <Box
                        component="td"
                        sx={{ ...cellRightSx, color: diffColor }}
                      >
                        {a.diff > 0 ? `+${fmt(a.diff)}` : fmt(a.diff)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const cellSx = {
  px: 1.25,
  py: 0.5,
  borderRight: `1px solid ${colors.border.light}`,
  whiteSpace: "nowrap" as const,
};
const cellRightSx = {
  ...cellSx,
  textAlign: "right" as const,
  fontVariantNumeric: "tabular-nums" as const,
};

export default CustomCsvUploadModal;
